import {useState, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {EntitySearchSelect} from '@/components/entity-search-select'
import {MicroLabel} from '@/components/period-selector'
import {
  useDesligarNorma,
  useLigarNorma,
  useNormasLigables,
  type Fila,
  type NormaLigada,
} from '@/hooks/sgsst/riesgos/use-ipevr'
import {
  useDesligarRiesgo,
  useLigarRiesgo,
  useRiesgosLigables,
  type LegalItem,
  type RiesgoLigado,
} from '@/hooks/sgsst/matriz_legal/use-matriz-legal'
import {translateApiError} from '@/lib/errors'

/** Vínculo norma ↔ riesgo (ítem 11) desde los dos lados; «marcado pero sin norma» invita a ligar, nunca es error. */

const RIESGO_TONE: Record<string, 'red' | 'orange' | 'amber' | 'lime' | 'neutral'> = {
  I: 'red',
  II: 'orange',
  III: 'amber',
  IV: 'lime',
}

/** Aterrizaje D4: las páginas destino destellan `?item=` y `?fila=` con `useHighlightParam`. */
function normaItemHref(businessId: string, legalItemId: string): string {
  return `/businesses/${businessId}/sgsst/matriz-legal?item=${legalItemId}`
}

function riesgoFilaHref(businessId: string, filaId: string): string {
  return `/businesses/${businessId}/sgsst/matriz-riesgos?fila=${filaId}`
}

/** Combobox y no `<select>` nativo (R3); el vacío dice a dónde ir porque las opciones vienen de otro registro. */
function LigarDialog<T>({
  open,
  onOpenChange,
  title,
  description,
  opciones,
  cargando,
  vacio,
  getKey,
  filterText,
  renderOption,
  placeholder,
  searchPlaceholder,
  noResultsLabel,
  confirmLabel,
  pending,
  onConfirm,
  testId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  opciones: T[]
  cargando: boolean
  vacio: ReactNode
  getKey: (item: T) => string
  filterText: (item: T) => string
  renderOption: (item: T) => ReactNode
  placeholder: string
  searchPlaceholder: string
  noResultsLabel: string
  confirmLabel: string
  pending: boolean
  onConfirm: (elegido: T) => Promise<unknown>
  testId: string
}) {
  const {t} = useTranslation()
  const [elegido, setElegido] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)

  function cerrar(next: boolean) {
    if (!next) {
      setElegido(null)
      setError(null)
    }
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={cerrar}>
      <DialogContent data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{description}</p>
        {cargando ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : opciones.length === 0 ? (
          <div className="text-sm text-muted-foreground" data-testid={`${testId}-vacio`}>
            {vacio}
          </div>
        ) : (
          <EntitySearchSelect
            items={opciones}
            value={elegido}
            getKey={getKey}
            filterText={filterText}
            renderOption={renderOption}
            renderTriggerValue={renderOption}
            placeholder={placeholder}
            searchPlaceholder={searchPlaceholder}
            noResultsLabel={noResultsLabel}
            testId={`${testId}-select`}
            onPick={setElegido}
          />
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => cerrar(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!elegido || pending}
            data-testid={`${testId}-confirmar`}
            onClick={async () => {
              setError(null)
              try {
                await onConfirm(elegido!)
                cerrar(false)
              } catch (e) {
                setError(translateApiError(e))
              }
            }}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function normaLabel(norma: NormaLigada): string {
  return norma.etiqueta
}

export function NormasDeLaFila({businessId, fila, canEdit}: {businessId: string; fila: Fila; canEdit: boolean}) {
  const {t} = useTranslation()
  const [ligando, setLigando] = useState(false)
  const {data: ligables, isLoading} = useNormasLigables(businessId, ligando)
  const ligar = useLigarNorma(businessId)
  const desligar = useDesligarNorma(businessId)
  const yaLigadas = new Set(fila.normas.map((n) => n.legal_item_id))
  const opciones = (ligables ?? []).filter((n) => !yaLigadas.has(n.legal_item_id))
  const sinEspecificar = fila.requisito_legal && fila.normas.length === 0

  return (
    <div className="space-y-2" data-testid="fila-normas">
      <MicroLabel>{t('ipevr.normas.title')}</MicroLabel>
      <div className="flex flex-wrap items-center gap-1.5">
        {fila.normas.map((norma) => (
          <span key={norma.legal_item_id} className="inline-flex items-center gap-1" data-testid="fila-norma-chip">
            <Link
              to={normaItemHref(businessId, norma.legal_item_id)}
              className="inline-flex items-center gap-1.5 rounded-md bg-muted/60 px-2 py-1 font-mono text-[11px] text-primary hover:underline"
              data-testid="fila-norma-link">
              {norma.etiqueta}
              {norma.es_propia && <Tag tone="neutral">{t('matrizLegal.propia.tag')}</Tag>}
            </Link>
            {canEdit && (
              <button
                type="button"
                aria-label={t('ipevr.normas.desligar', {norma: norma.etiqueta})}
                className="text-muted-foreground hover:text-destructive"
                disabled={desligar.isPending}
                data-testid="fila-norma-desligar"
                onClick={() => desligar.mutate({filaId: fila.id, legalItemId: norma.legal_item_id})}>
                ×
              </button>
            )}
          </span>
        ))}
        {fila.normas.length === 0 && (
          <span
            className={sinEspecificar ? 'tone-amber rounded px-1.5 py-0.5 text-xs' : 'text-xs text-muted-foreground'}
            data-testid={sinEspecificar ? 'fila-requisito-sin-especificar' : 'fila-sin-normas'}>
            {sinEspecificar ? t('ipevr.normas.sinEspecificar') : t('ipevr.normas.ninguna')}
          </span>
        )}
        {canEdit && (
          <Button size="sm" variant="outline" data-testid="fila-ligar-norma" onClick={() => setLigando(true)}>
            {t('ipevr.normas.ligar')}
          </Button>
        )}
      </div>
      <LigarDialog
        open={ligando}
        onOpenChange={setLigando}
        title={t('ipevr.normas.dialogTitle')}
        description={t('ipevr.normas.dialogBody', {peligro: fila.descripcion})}
        opciones={opciones}
        cargando={isLoading}
        vacio={t('ipevr.normas.sinOpciones')}
        getKey={(n) => n.legal_item_id}
        filterText={(n) => `${n.etiqueta} ${n.norm_ref}`}
        renderOption={(n) => <span className="font-mono text-xs">{normaLabel(n)}</span>}
        placeholder={t('ipevr.normas.selectPlaceholder')}
        searchPlaceholder={t('ipevr.normas.selectSearch')}
        noResultsLabel={t('ipevr.normas.selectSinResultados')}
        confirmLabel={t('ipevr.normas.confirmar')}
        pending={ligar.isPending}
        onConfirm={(n) => ligar.mutateAsync({filaId: fila.id, legalItemId: n.legal_item_id})}
        testId="ligar-norma-dialog"
      />
    </div>
  )
}

export function RiesgosDeLaNorma({
  businessId,
  item,
  readOnly,
}: {
  businessId: string
  item: LegalItem
  readOnly: boolean
}) {
  const {t} = useTranslation()
  const [ligando, setLigando] = useState(false)
  const {data: ligables, isLoading} = useRiesgosLigables(businessId, ligando)
  const ligar = useLigarRiesgo(businessId)
  const desligar = useDesligarRiesgo(businessId)
  const yaLigados = new Set(item.riesgos.map((r) => r.riesgo_fila_id))
  const opciones = (ligables ?? []).filter((r) => !yaLigados.has(r.riesgo_fila_id))

  return (
    <div className="mt-2 space-y-1.5" data-testid="item-riesgos">
      <div className="flex flex-wrap items-center gap-1.5">
        <MicroLabel>{t('matrizLegal.riesgos.title')}</MicroLabel>
        {item.riesgos.map((riesgo) => (
          <span key={riesgo.riesgo_fila_id} className="inline-flex items-center gap-1" data-testid="item-riesgo-chip">
            <Link
              to={riesgoFilaHref(businessId, riesgo.riesgo_fila_id)}
              className="inline-flex max-w-[18rem] items-center gap-1.5 truncate rounded-md bg-muted/60 px-2 py-1 text-[11px] text-primary hover:underline"
              data-testid="item-riesgo-link">
              {riesgo.interpretacion && (
                <Tag tone={RIESGO_TONE[riesgo.interpretacion] ?? 'neutral'}>{riesgo.interpretacion}</Tag>
              )}
              <span className="truncate">{riesgo.descripcion}</span>
            </Link>
            {!readOnly && (
              <button
                type="button"
                aria-label={t('matrizLegal.riesgos.desligar', {peligro: riesgo.descripcion})}
                className="text-muted-foreground hover:text-destructive"
                disabled={desligar.isPending}
                data-testid="item-riesgo-desligar"
                onClick={() => desligar.mutate({itemId: item.id, filaId: riesgo.riesgo_fila_id})}>
                ×
              </button>
            )}
          </span>
        ))}
        {item.riesgos.length === 0 && (
          <span className="text-xs text-muted-foreground" data-testid="item-sin-riesgos">
            {t('matrizLegal.riesgos.ninguno')}
          </span>
        )}
        {!readOnly && (
          <Button size="sm" variant="outline" data-testid="item-ligar-riesgo" onClick={() => setLigando(true)}>
            {t('matrizLegal.riesgos.ligar')}
          </Button>
        )}
      </div>
      <LigarDialog
        open={ligando}
        onOpenChange={setLigando}
        title={t('matrizLegal.riesgos.dialogTitle')}
        description={t('matrizLegal.riesgos.dialogBody', {norma: item.titulo})}
        opciones={opciones}
        cargando={isLoading}
        vacio={t('matrizLegal.riesgos.sinOpciones')}
        getKey={(r: RiesgoLigado) => r.riesgo_fila_id}
        filterText={(r) => r.descripcion}
        renderOption={(r) => <span className="text-sm">{r.descripcion}</span>}
        placeholder={t('matrizLegal.riesgos.selectPlaceholder')}
        searchPlaceholder={t('matrizLegal.riesgos.selectSearch')}
        noResultsLabel={t('matrizLegal.riesgos.selectSinResultados')}
        confirmLabel={t('matrizLegal.riesgos.confirmar')}
        pending={ligar.isPending}
        onConfirm={(r) => ligar.mutateAsync({itemId: item.id, filaId: r.riesgo_fila_id})}
        testId="ligar-riesgo-dialog"
      />
    </div>
  )
}
